import { Box, MultiSelect, Text } from '@mantine/core'
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import classes from '../LogVolunteeringActivityForm/LogVolunteeringActivityForm.module.scss'

import { FakeCheckbox, Flex, FormField, Icon, IconProps, Loader } from '@percent/lemonade'
import { useQuery } from '@percent/workplace-giving/common/hooks'
import { getVolunteeringActivities } from '@percent/workplace-giving/api/volunteering/getVolunteeringActivities/getVolunteeringActivities'
import { getVolunteeringSkills } from '@percent/workplace-giving/api/volunteering/getVolunteeringSkills/getVolunteeringSkills'

type ActivityTagsSectionProps = Readonly<{
  activitiesValue: string[]
  skillsValue: string[]
  onActivitiesChange: (values: string[]) => void
  onSkillsChange: (values: string[]) => void
  activitiesTouched?: boolean
  activitiesError?: string
  onActivitiesTouch: () => void
}>

export function ActivityTagsSection({
  activitiesValue,
  skillsValue,
  onActivitiesChange,
  onSkillsChange,
  activitiesTouched,
  activitiesError,
  onActivitiesTouch
}: ActivityTagsSectionProps) {
  const { t } = useTranslation()
  const [activitiesDropdownIcon, setActivitiesDropdownIcon] = useState<IconProps['name']>('dropdown-arrow-down')
  const [skillsDropdownIcon, setSkillsDropdownIcon] = useState<IconProps['name']>('dropdown-arrow-down')

  const { data: activitiesData, isPending: isLoadingActivities } = useQuery(
    ['getVolunteeringActivities'],
    getVolunteeringActivities
  )

  const { data: skillsData, isPending: isLoadingSkills } = useQuery(['getVolunteeringSkills'], getVolunteeringSkills)

  const activitiesOptions = useMemo(() => {
    if (!activitiesData) return []

    return activitiesData
      .map(activity => ({ value: activity.id, label: activity.name }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [activitiesData])

  const skillsOptions = useMemo(() => {
    if (!skillsData) return []

    return skillsData
      .map(skill => ({ value: skill.id, label: skill.name }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [skillsData])

  return (
    <Box>
      <Text className={classes.SectionTitle}>{t('workplace_giving.volunteering.activityForm.volunteerActivity')}</Text>
      {isLoadingActivities || isLoadingSkills ? (
        <Loader />
      ) : (
        <>
          <FormField
            label={t('workplace_giving.volunteering.activityForm.activityTags.label')}
            necessity="required"
            data-testid="activitiesField"
            status={activitiesTouched && activitiesError ? 'danger' : 'default'}
            statusMessage={activitiesError}
          >
            <MultiSelect
              className={classes.MantineMultiselect}
              wrapperProps={{
                'data-error': Boolean(activitiesTouched && activitiesError)
              }}
              style={{ width: '100%' }}
              placeholder={
                !activitiesValue?.length ? t('workplace_giving.volunteering.activityForm.activityTags.placeholder') : ''
              }
              searchable
              data={activitiesOptions}
              value={activitiesValue}
              onChange={onActivitiesChange}
              rightSection={<Icon name={activitiesDropdownIcon} size={8} color="gray600" />}
              onDropdownOpen={() => setActivitiesDropdownIcon('dropdown-arrow-up')}
              onDropdownClose={() => setActivitiesDropdownIcon('dropdown-arrow-down')}
              onClick={onActivitiesTouch}
              renderOption={item => (
                <Flex justify="center" align="center">
                  <FakeCheckbox isSelected={!!item.checked} />
                  <div style={{ marginLeft: '0.5rem' }}>{item.option.label}</div>
                </Flex>
              )}
            />
          </FormField>
          <FormField
            label={t('workplace_giving.wizard.skills.label')}
            necessity="optional"
            optionalText={t('workplace_giving.validation.optional')}
            data-testid="skillsField"
          >
            <MultiSelect
              className={classes.MantineMultiselect}
              style={{ width: '100%' }}
              placeholder={!skillsValue?.length ? 'e.g. Graphic design' : ''}
              searchable
              data={skillsOptions}
              value={skillsValue}
              onChange={onSkillsChange}
              rightSection={<Icon name={skillsDropdownIcon} size={8} color="gray600" />}
              onDropdownOpen={() => setSkillsDropdownIcon('dropdown-arrow-up')}
              onDropdownClose={() => setSkillsDropdownIcon('dropdown-arrow-down')}
              renderOption={item => (
                <Flex justify="center" align="center">
                  <FakeCheckbox isSelected={!!item.checked} />
                  <div style={{ marginLeft: '0.5rem' }}>{item.option.label}</div>
                </Flex>
              )}
            />
          </FormField>
        </>
      )}
    </Box>
  )
}
